import type {
  CreateManualCfrResult,
  ManualCfrStatus,
} from "@/lib/api/cfr";

export type ManualCfrUploadPhase =
  | "editing"
  | "creating"
  | "uploading"
  | "confirming"
  | "discarding"
  | "failed"
  | "complete";

export type ManualCfrUploadFailedStep =
  | "creating"
  | "uploading"
  | "confirming"
  | "discarding";

export interface ManualCfrUploadFormState {
  template_id: string;
  customer_id: string;
  last_status: ManualCfrStatus | "";
  document_title: string;
  contract_number: string;
  contract_type: string;
  contract_quantity: string;
  contract_shipping_dates: string;
  contract_date: string;
  final_document: File | null;
  sent_document: File | null;
}

export interface ManualCfrUploadState {
  phase: ManualCfrUploadPhase;
  form: ManualCfrUploadFormState;
  createResult: CreateManualCfrResult | null;
  uploadProgress: {
    done: number;
    total: number;
    currentFilename: string;
  };
  errorMessage: string;
  failedStep: ManualCfrUploadFailedStep | null;
  fileInputKey: number;
}

export type ManualCfrUploadAction =
  | { type: "PATCH_FORM"; patch: Partial<ManualCfrUploadFormState> }
  | { type: "VALIDATION_FAILED"; message: string }
  | { type: "START_CREATE" }
  | { type: "CREATE_SUCCEEDED"; result: CreateManualCfrResult }
  | { type: "START_UPLOAD"; total: number; currentFilename: string }
  | { type: "UPLOAD_PROGRESS"; done: number; currentFilename: string }
  | { type: "START_CONFIRM" }
  | { type: "START_DISCARD" }
  | { type: "FAIL"; step: ManualCfrUploadFailedStep; message: string }
  | { type: "RETRY" }
  | { type: "COMPLETE" }
  | { type: "RESET" };

const emptyForm: ManualCfrUploadFormState = {
  template_id: "",
  customer_id: "",
  last_status: "completed" as const,
  document_title: "",
  contract_number: "",
  contract_type: "",
  contract_quantity: "",
  contract_shipping_dates: "",
  contract_date: "",
  final_document: null,
  sent_document: null,
};

export const initialManualCfrUploadState: ManualCfrUploadState = {
  phase: "editing",
  form: emptyForm,
  createResult: null,
  uploadProgress: {
    done: 0,
    total: 0,
    currentFilename: "",
  },
  errorMessage: "",
  failedStep: null,
  fileInputKey: 0,
};

export function manualCfrUploadReducer(
  state: ManualCfrUploadState,
  action: ManualCfrUploadAction,
): ManualCfrUploadState {
  switch (action.type) {
    case "PATCH_FORM":
      return {
        ...state,
        form: { ...state.form, ...action.patch },
        errorMessage: "",
      };
    case "VALIDATION_FAILED":
      return {
        ...state,
        phase: "editing",
        errorMessage: action.message,
        failedStep: null,
      };
    case "START_CREATE":
      return {
        ...state,
        phase: "creating",
        errorMessage: "",
        failedStep: null,
      };
    case "CREATE_SUCCEEDED":
      return {
        ...state,
        createResult: action.result,
        errorMessage: "",
        failedStep: null,
      };
    case "START_UPLOAD":
      return {
        ...state,
        phase: "uploading",
        uploadProgress: {
          done: 0,
          total: action.total,
          currentFilename: action.currentFilename,
        },
        errorMessage: "",
        failedStep: null,
      };
    case "UPLOAD_PROGRESS":
      return {
        ...state,
        uploadProgress: {
          ...state.uploadProgress,
          done: action.done,
          currentFilename: action.currentFilename,
        },
      };
    case "START_CONFIRM":
      return {
        ...state,
        phase: "confirming",
        errorMessage: "",
        failedStep: null,
      };
    case "START_DISCARD":
      return {
        ...state,
        phase: "discarding",
        errorMessage: "",
        failedStep: null,
      };
    case "FAIL":
      return {
        ...state,
        phase: "failed",
        errorMessage: action.message,
        failedStep: action.step,
      };
    case "RETRY":
      return {
        ...state,
        phase: state.failedStep ?? "editing",
        errorMessage: "",
      };
    case "COMPLETE":
      return {
        ...state,
        phase: "complete",
        errorMessage: "",
        failedStep: null,
      };
    case "RESET":
      return {
        ...initialManualCfrUploadState,
        form: { ...emptyForm },
        fileInputKey: state.fileInputKey + 1,
      };
  }
}
